import { createContext, useContext, useState, ReactNode } from 'react';

// Définition de l'interface pour notre contexte
interface SidebarContextType {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

// Création du contexte avec des valeurs par défaut
const SidebarContext = createContext<SidebarContextType>({
  sidebarOpen: true,
  toggleSidebar: () => {},
  setSidebarOpen: () => {},
});

// Hook personnalisé pour utiliser notre contexte
export const useSidebar = () => useContext(SidebarContext);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // État pour savoir si la sidebar est ouverte ou non
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Détecter si l'appareil est mobile (écran < 768px)
    const isMobile = window.innerWidth <= 768;
    
    // Sur mobile: toujours fermé par défaut, quelle que soit la valeur stockée
    if (isMobile) {
      return false;
    }
    
    // Sur desktop: utiliser la valeur stockée ou ouvrir par défaut
    const savedState = localStorage.getItem('sidebarOpen');
    return savedState !== null ? savedState === 'true' : true;
  });

  // Fonction pour changer l'état de la sidebar
  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    
    // Ne sauvegarder l'état que sur desktop
    if (window.innerWidth > 768) {
      localStorage.setItem('sidebarOpen', String(newState));
    }
  };

  // Fonction pour définir directement l'état de la sidebar
  const setSidebarOpenDirectly = (open: boolean) => {
    setSidebarOpen(open);
    
    // Ne sauvegarder l'état que sur desktop
    if (window.innerWidth > 768) {
      localStorage.setItem('sidebarOpen', String(open));
    }
  };

  return (
    <SidebarContext.Provider value={{ sidebarOpen, toggleSidebar, setSidebarOpen: setSidebarOpenDirectly }}>
      {children}
    </SidebarContext.Provider>
  );
}
